import { cn } from '@lib/utils';
import * as Popover from '@radix-ui/react-popover';
import { AlertCircle, Check, ChevronDown, Search } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

// --- Country data (top 31 by WhatsApp usage + common) -----------------------

interface Country {
  code: string; // ISO 3166-1 alpha-2
  name: string;
  dial: string; // e.g. "+91"
  flag: string; // emoji flag
}

const COUNTRIES: Country[] = [
  { code: 'IN', name: 'India', dial: '+91', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: 'US', name: 'United States', dial: '+1', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'GB', name: 'United Kingdom', dial: '+44', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'AE', name: 'UAE', dial: '+971', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: 'SA', name: 'Saudi Arabia', dial: '+966', flag: '\u{1F1F8}\u{1F1E6}' },
  { code: 'SG', name: 'Singapore', dial: '+65', flag: '\u{1F1F8}\u{1F1EC}' },
  { code: 'AU', name: 'Australia', dial: '+61', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: 'CA', name: 'Canada', dial: '+1', flag: '\u{1F1E8}\u{1F1E6}' },
  { code: 'DE', name: 'Germany', dial: '+49', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'FR', name: 'France', dial: '+33', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'BR', name: 'Brazil', dial: '+55', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: 'MX', name: 'Mexico', dial: '+52', flag: '\u{1F1F2}\u{1F1FD}' },
  { code: 'ID', name: 'Indonesia', dial: '+62', flag: '\u{1F1EE}\u{1F1E9}' },
  { code: 'NG', name: 'Nigeria', dial: '+234', flag: '\u{1F1F3}\u{1F1EC}' },
  { code: 'PK', name: 'Pakistan', dial: '+92', flag: '\u{1F1F5}\u{1F1F0}' },
  { code: 'BD', name: 'Bangladesh', dial: '+880', flag: '\u{1F1E7}\u{1F1E9}' },
  { code: 'PH', name: 'Philippines', dial: '+63', flag: '\u{1F1F5}\u{1F1ED}' },
  { code: 'EG', name: 'Egypt', dial: '+20', flag: '\u{1F1EA}\u{1F1EC}' },
  { code: 'ZA', name: 'South Africa', dial: '+27', flag: '\u{1F1FF}\u{1F1E6}' },
  { code: 'KE', name: 'Kenya', dial: '+254', flag: '\u{1F1F0}\u{1F1EA}' },
  { code: 'MY', name: 'Malaysia', dial: '+60', flag: '\u{1F1F2}\u{1F1FE}' },
  { code: 'TH', name: 'Thailand', dial: '+66', flag: '\u{1F1F9}\u{1F1ED}' },
  { code: 'JP', name: 'Japan', dial: '+81', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: 'KR', name: 'South Korea', dial: '+82', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: 'IT', name: 'Italy', dial: '+39', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: 'ES', name: 'Spain', dial: '+34', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'NL', name: 'Netherlands', dial: '+31', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: 'TR', name: 'Turkey', dial: '+90', flag: '\u{1F1F9}\u{1F1F7}' },
  { code: 'RU', name: 'Russia', dial: '+7', flag: '\u{1F1F7}\u{1F1FA}' },
  { code: 'LK', name: 'Sri Lanka', dial: '+94', flag: '\u{1F1F1}\u{1F1F0}' },
  { code: 'NP', name: 'Nepal', dial: '+977', flag: '\u{1F1F3}\u{1F1F5}' },
];

// --- Component ---------------------------------------------------------------

export interface PhoneInputProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** E.164 value (e.g. "+919876543210"). Controlled. */
  value?: string;
  onChange?: (e164: string) => void;
  defaultCountry?: string;
  placeholder?: string;
  disabled?: boolean;
  containerClassName?: string;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  function PhoneInput(
    {
      label,
      hint,
      error,
      value = '',
      onChange,
      defaultCountry = 'IN',
      placeholder = 'Phone number',
      disabled,
      containerClassName,
    },
    ref,
  ) {
    const reactId = useId();
    const hasError = Boolean(error);

    // Parse value into country + local
    const parseValue = useCallback(
      (val: string): { country: Country; local: string } => {
        if (val.startsWith('+')) {
          // Try longest dial-code match first
          const sorted = [...COUNTRIES].sort(
            (a, b) => b.dial.length - a.dial.length,
          );
          for (const c of sorted) {
            if (val.startsWith(c.dial)) {
              return { country: c, local: val.slice(c.dial.length) };
            }
          }
        }
        const india = COUNTRIES[0];
        const fallback =
          COUNTRIES.find((c) => c.code === defaultCountry) ?? india;
        return { country: fallback, local: val.replace(/^\+\d+/, '') };
      },
      [defaultCountry],
    );

    const parsed = useMemo(() => parseValue(value), [value, parseValue]);
    const [selectedCountry, setSelectedCountry] = useState<Country>(
      parsed.country,
    );
    const [localNumber, setLocalNumber] = useState(parsed.local);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const emitChange = useCallback(
      (country: Country, local: string) => {
        const digits = local.replace(/\D/g, '');
        onChange?.(digits ? `${country.dial}${digits}` : '');
      },
      [onChange],
    );

    const handleCountrySelect = (country: Country): void => {
      setSelectedCountry(country);
      setOpen(false);
      setSearch('');
      emitChange(country, localNumber);
    };

    const handleLocalChange = (raw: string): void => {
      const cleaned = raw.replace(/[^\d\s]/g, '');
      setLocalNumber(cleaned);
      emitChange(selectedCountry, cleaned);
    };

    const filtered = useMemo(() => {
      if (!search) return COUNTRIES;
      const q = search.toLowerCase();
      return COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.dial.includes(q) ||
          c.code.toLowerCase().includes(q),
      );
    }, [search]);

    return (
      <div className={cn('flex w-full flex-col gap-1.5', containerClassName)}>
        {label ? (
          <label
            htmlFor={reactId}
            className="text-label text-apple-label-secondary"
          >
            {label}
          </label>
        ) : null}

        <div
          className={cn(
            'flex items-center rounded-lg transition-all duration-200 ease-apple',
            hasError
              ? 'bg-[#FFF1F2] ring-3 ring-[#FDA4AF]/40'
              : 'bg-apple-gray-100 focus-within:bg-white focus-within:ring-3 focus-within:ring-link/20',
            disabled && 'pointer-events-none opacity-60',
          )}
        >
          {/* Country selector — Radix Popover (portaled, no layout shift) */}
          <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
              <button
                type="button"
                aria-label={`Country: ${selectedCountry.name}`}
                className={cn(
                  'flex h-12 shrink-0 items-center gap-2 rounded-l-lg px-3 transition',
                  'hover:bg-apple-gray-200/50 focus:outline-none focus-visible:bg-apple-gray-200/60',
                  'border-r border-apple-divider',
                )}
              >
                <span
                  aria-hidden
                  className="text-[20px] leading-none select-none"
                >
                  {selectedCountry.flag}
                </span>
                <span className="text-[14px] font-semibold tracking-apple-body text-apple-label tabular-nums">
                  {selectedCountry.dial}
                </span>
                <ChevronDown
                  size={14}
                  strokeWidth={1.75}
                  className={cn(
                    'text-apple-gray-500 transition-transform duration-200',
                    open && 'rotate-180 text-apple-label-secondary',
                  )}
                />
              </button>
            </Popover.Trigger>

            <Popover.Portal>
              <Popover.Content
                align="start"
                sideOffset={8}
                className={cn(
                  'z-[150] w-[320px] overflow-hidden rounded-xl border border-apple-divider bg-white shadow-floating',
                  'data-[state=open]:animate-slide-down',
                  'origin-top',
                )}
                onOpenAutoFocus={(e) => {
                  // Let the search input auto-focus; prevent body focus steal.
                  e.preventDefault();
                  const searchEl =
                    document.getElementById(`${reactId}-country-search`);
                  searchEl?.focus();
                }}
              >
                {/* Search */}
                <div className="border-b border-apple-divider p-2">
                  <div className="flex items-center gap-2 rounded-lg bg-apple-gray-100 px-3 py-2">
                    <Search
                      size={14}
                      strokeWidth={1.75}
                      className="shrink-0 text-apple-gray-500"
                    />
                    <input
                      id={`${reactId}-country-search`}
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search country or code…"
                      className="min-w-0 flex-1 bg-transparent text-[13px] text-apple-label placeholder:text-apple-placeholder outline-none"
                    />
                  </div>
                </div>

                {/* Country list */}
                <div className="max-h-[320px] overflow-y-auto py-1">
                  {filtered.length === 0 ? (
                    <div className="px-4 py-8 text-center text-[13px] text-apple-gray-500">
                      No countries found
                    </div>
                  ) : (
                    filtered.map((c) => {
                      const active = c.code === selectedCountry.code;
                      return (
                        <button
                          key={c.code}
                          type="button"
                          onClick={() => handleCountrySelect(c)}
                          className={cn(
                            'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors duration-150',
                            active
                              ? 'bg-[#F0FDF4] text-emerald-700'
                              : 'text-apple-label hover:bg-apple-gray-100',
                          )}
                        >
                          <span
                            aria-hidden
                            className="text-[20px] leading-none select-none"
                          >
                            {c.flag}
                          </span>
                          <span
                            className={cn(
                              'min-w-0 flex-1 truncate text-[13.5px]',
                              active ? 'font-semibold' : 'font-medium',
                            )}
                          >
                            {c.name}
                          </span>
                          <span
                            className={cn(
                              'text-[12.5px] tabular-nums',
                              active
                                ? 'font-semibold text-emerald-700'
                                : 'text-apple-gray-500',
                            )}
                          >
                            {c.dial}
                          </span>
                          {active ? (
                            <Check
                              size={14}
                              strokeWidth={2.5}
                              className="text-emerald-600"
                            />
                          ) : (
                            <span className="w-[14px]" aria-hidden />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>

          {/* Number input */}
          <input
            ref={ref}
            id={reactId}
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            value={localNumber}
            onChange={(e) => handleLocalChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className="min-w-0 flex-1 bg-transparent px-4 py-3 text-[15px] text-apple-label placeholder:text-apple-placeholder outline-none"
            aria-invalid={hasError}
          />
        </div>

        {hasError ? (
          <p className="flex items-center gap-1.5 text-[12px] font-medium text-rose-600">
            <AlertCircle size={13} strokeWidth={1.75} />
            {error}
          </p>
        ) : hint ? (
          <p className="text-[12px] text-apple-gray-500">{hint}</p>
        ) : null}
      </div>
    );
  },
);
